import { isAtom, isDvalaFunction, isEffect, isMatrix, isRegularExpression } from "@mojir/dvala-types";
//#region src/utils.ts
function stringifyValue(value, html) {
	const gt = html ? "&gt;" : ">";
	const lt = html ? "&lt;" : "<";
	if (isDvalaFunction(value)) {
		if (value.functionType === "Builtin") return `${lt}builtin function ${value.normalBuiltinSymbolType}${gt}`;
		return `${lt}${value.functionType === "Macro" ? "macro" : "function"} ${"name" in value && value.name ? value.name : "λ"}${gt}`;
	}
	if (value === null) return "null";
	if (typeof value === "object" && value instanceof Error) return value.toString();
	if (typeof value === "object" && value instanceof RegExp) return `${value}`;
	if (typeof value === "number") return `${value}`;
	if (isAtom(value)) return `:${value.name}`;
	if (isEffect(value)) return `${lt}effect ${value.name}${gt}`;
	if (isRegularExpression(value)) return `/${value.s}/${value.f}`;
	if (typeof value === "string") return `"${value}"`;
	if (Array.isArray(value) && isMatrix(value)) return stringifyMatrix(value);
	return smartStringify(replaceInfinities(value), 0);
}
const MAX_WIDTH = 80;
const INDENT = 2;
/**
* Smart JSON-like stringifier that inlines flat arrays and objects
* when they fit within MAX_WIDTH, and breaks to multi-line when they don't.
*/
function smartStringify(value, indent) {
	if (value === null) return "null";
	if (typeof value === "boolean") return String(value);
	if (typeof value === "number") return String(value);
	if (typeof value === "string") return JSON.stringify(value);
	if (isAtom(value)) return `:${value.name}`;
	if (Array.isArray(value)) {
		if (value.length === 0) return "[]";
		if (!value.some((v) => v !== null && typeof v === "object")) {
			const flat = `[${value.map((v) => smartStringify(v, indent)).join(", ")}]`;
			if (indent * INDENT + flat.length <= MAX_WIDTH) return flat;
		}
		const pad = " ".repeat((indent + 1) * INDENT);
		return `[\n${value.map((v) => `${pad}${smartStringify(v, indent + 1)}`).join(",\n")}\n${" ".repeat(indent * INDENT)}]`;
	}
	if (typeof value === "object") {
		const entries = Object.entries(value);
		if (entries.length === 0) return "{}";
		const flat = `{ ${entries.map(([k, v]) => `${JSON.stringify(k)}: ${smartStringify(v, indent)}`).join(", ")} }`;
		if (indent * INDENT + flat.length <= MAX_WIDTH) return flat;
		const pad = " ".repeat((indent + 1) * INDENT);
		return `{\n${entries.map(([k, v]) => `${pad}${JSON.stringify(k)}: ${smartStringify(v, indent + 1)}`).join(",\n")}\n${" ".repeat(indent * INDENT)}}`;
	}
	return String(value);
}
function replaceInfinities(value) {
	if (value === Number.POSITIVE_INFINITY) return "∞";
	if (value === Number.NEGATIVE_INFINITY) return "-∞";
	if (Array.isArray(value)) return value.map(replaceInfinities);
	if (typeof value === "object" && value !== null) {
		const result = {};
		for (const [key, val] of Object.entries(value)) result[key] = replaceInfinities(val);
		return result;
	}
	return value;
}
function stringifyMatrix(matrix) {
	const padding = matrix.flat().reduce((max, cell) => Math.max(max, `${cell}`.length), 0) + 1;
	return matrix.map((row) => `[${row.map((cell) => `${cell}`.padStart(padding)).join(" ")} ]`).join("\n");
}
function findAllOccurrences(input, pattern) {
	const matches = [...input.matchAll(pattern)];
	return new Set(matches.map((match) => match[0]));
}
//#endregion
export { findAllOccurrences, stringifyValue };

//# sourceMappingURL=utils.js.map